/**
 * WorkflowHelp.js
 *
 * Interactive estimating workflow stepper — the step-by-step business process
 * for building a cost breakdown estimate. Tab badges point at the portal page
 * where each step happens.
 */

import React, { useState } from 'react';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

const WORKFLOW_STEPS = [
  { responsible: 'Lead/Mgr', activity: 'Analyze Scope; Determine level of interest', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Start Reviewing T&Cs\n- Safety\n- Commercial\n- QA/QC\n- Schedule & Reporting', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Develop Overall Approach (Self vs Subs); (Large M.D. Project vs Conventional); (Client MTO vs Conventional Take-Off); etc.', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Analyse Taxes and Duties Requirements ("Client Exemptions", "Sales & Use Tax", "Self-Assessed Taxes", "Other Taxes", "Duties", etc.)', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Determine Potential Sources of Material Procurement (Local to Project Site vs "Out-of-State"; Impact of "S.A. Taxes", "Duties", etc.)', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Determine where Fabrications will be executed (Local to Project Site vs "Out-of-State"; Impact of "S.A. Taxes", "Duties", etc.).', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Apply Currency Management Option(s).', tab: 'Curr. Mgmt', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Develop Internal WBS', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Assign Estimating Team', tab: '', notes: '' },
  { responsible: 'Lead/Sched.', activity: 'Start Developing Schedule', tab: '', notes: '' },
  { responsible: 'Estim. Team', activity: 'Fill-out Client/Project Information', tab: 'CB Summary', notes: '' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Labour Rate" tab', tab: 'Labour Rate', notes: '' },
  { responsible: 'Estim. Team', activity: 'Sort-out Quantities', tab: '', notes: 'See also MTO tabs' },
  { responsible: 'Estim. Team', activity: 'Go out for Quotes on Material', tab: '', notes: '' },
  { responsible: 'Estim. Team', activity: 'Go out for Quotes on Subs', tab: '', notes: '' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Direct Labour" section in the different "Discipline" tabs. Assign Distribution Ratio for "Phases" and "Shifts"', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Other Direct Labour" section in the different "Discipline" tabs. Assign Distribution Ratio for "Phases" and "Shifts"', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Supervision Labour" section. Determine if there will be any "Working Foremen". Also, any ratio of "H.O. Personnel" vs "Local"', tab: 'Various Disc.', notes: 'See also Indirects tab' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Staff Labour" section. Verify any Duplication or Omission', tab: 'Various Disc.', notes: 'See also Indirects tab' },
  { responsible: 'Estim. Team', activity: 'Fill-out "Non-Labour" sections in the different "Discipline" tabs', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: '- Materials & Specialty Materials', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: '- Subs', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: '- Rentals', tab: 'Various Disc.', notes: 'See also Rentals tab' },
  { responsible: 'Estim. Team', activity: '- Inspection', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: '- R&B (Direct and Foremen/GF-LOCAL)', tab: 'Various Disc.', notes: 'See also Labour Rates tab' },
  { responsible: 'Estim. Team', activity: '- R&B (Supervision/GF from HO and Staff)', tab: 'Various Disc.', notes: 'See also Indirects tab' },
  { responsible: 'Estim. Team', activity: '- Travel & Meals', tab: 'Various Disc.', notes: '' },
  { responsible: 'Estim. Team', activity: '- Site Expenses (incl. vehicles)', tab: 'Various Disc.', notes: 'See also Site Expenses & Indirects tabs' },
  { responsible: 'Estim. Team', activity: '- Determine "Self-Assessed Taxes" & "Duties", and distribute above', tab: 'Various Disc.', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Complete "CB Summary" tab', tab: 'CB Summary', notes: '' },
  { responsible: 'Lead/Mgr', activity: '- Conversion Rate', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: '- Mark-ups', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: '- Sales & Use Tax', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: '- Stab Funds', tab: '', notes: '' },
  { responsible: 'Lead/Mgr', activity: 'Develop "Client WBS" tab', tab: 'Client WBS', notes: '' },
];

const RESPONSIBLE_COLORS = {
  'Lead/Mgr': { color: '#7c3aed', bg: '#f5f3ff' },
  'Lead/Sched.': { color: '#0891b2', bg: '#ecfeff' },
  'Estim. Team': { color: '#ea580c', bg: '#fff7ed' },
};

const WorkflowHelp = ({ isOpen, onClose }) => {
  const [expandedStep, setExpandedStep] = useState(null);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}>
      <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1f2937' }}>Estimating Workflow</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-audio-skip>
              <ListenButton compact guideId="cb-workflow-help" title="Estimating Workflow" accent="#7c3aed" />
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
          </div>
          <p style={{ margin: '8px 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
            Step-by-step process for creating a cost breakdown estimate. {WORKFLOW_STEPS.length} steps total.
          </p>
          <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
            {Object.entries(RESPONSIBLE_COLORS).map(([key, val]) => (
              <span key={key} style={{ padding: '4px 10px', borderRadius: '12px', backgroundColor: val.bg, color: val.color, fontWeight: '600', fontSize: '0.75rem' }}>
                {key}
              </span>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div style={{ overflowY: 'auto', padding: '16px 28px 24px', flex: 1 }}>
          <HelpWithVideoTour guideId="cb-workflow-help" title="Estimating Workflow" accent="#7c3aed">
          {WORKFLOW_STEPS.map((step, idx) => {
            const rc = RESPONSIBLE_COLORS[step.responsible] || { color: '#6b7280', bg: '#f3f4f6' };
            const isExpanded = expandedStep === idx;
            return (
              <div key={idx}
                onClick={() => setExpandedStep(isExpanded ? null : idx)}
                style={{
                  display: 'flex', gap: '16px', padding: '12px 0',
                  borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  alignItems: 'flex-start'
                }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%', backgroundColor: rc.bg,
                  color: rc.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '700', fontSize: '0.8rem', flexShrink: 0, border: `2px solid ${rc.color}`
                }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '8px', backgroundColor: rc.bg, color: rc.color, fontWeight: '600', fontSize: '0.7rem' }}>
                      {step.responsible}
                    </span>
                    {step.tab && (
                      <span style={{ padding: '2px 8px', borderRadius: '8px', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.7rem', fontWeight: '500' }}>
                        {step.tab}
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#1f2937', fontSize: '0.9rem', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                    {step.activity}
                  </div>
                  {isExpanded && step.notes && (
                    <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#fffbeb', borderRadius: '6px', fontSize: '0.85rem', color: '#92400e' }}>
                      {step.notes}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </HelpWithVideoTour>
        </div>
      </div>
    </div>
  );
};

export default WorkflowHelp;
